import { useEffect, useState } from "react";
import { Crown } from "lucide-react";
import { api, errMsg, useAct, useGet } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtStamp } from "../lib/util";
import { UserLogo } from "../components/Logos";

const EMOJI = ["❤️", "😍", "🥰", "😂", "🥹", "😘", "🔥", "👏", "🙏", "🌻", "👑", "🐶", "😊", "🤗", "💯", "✨"];

export function PinSetup() {
  const { me } = useAuth();
  const act = useAct();
  const [f, setF] = useState({ new: "", confirm: "" });
  const [err, setErr] = useState("");
  const save = async () => {
    if (f.new !== f.confirm) return setErr("New PINs don't match");
    try {
      await act("post", "/auth/pin", { new: f.new, confirm: f.confirm }, { ok: "PIN updated" });
      localStorage.setItem(`oh_pinlen_${me.id}`, String(f.new.length));
    } catch (e) { setErr(errMsg(e)); }
  };
  return (
    <div className="min-h-[100dvh] flex items-center justify-center p-6 pt-safe" data-testid="pin-setup-screen">
      <div className="w-full max-w-sm card p-6 rise">
        <div className="flex justify-center mb-4"><UserLogo id={me.id} size={56} /></div>
        <h2 className="display text-3xl text-center mb-1">Secure your account</h2>
        <p className="text-oh-text2 text-sm text-center mb-5">You signed in with a temporary PIN. Create your own 4–8 digit PIN to continue.</p>
        {[["new", "New PIN"], ["confirm", "Confirm new PIN"]].map(([k, l]) => (
          <input key={k} className="inp mb-3 tracking-[0.5em] text-center" type="password" inputMode="numeric" maxLength={8} placeholder={l}
            value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value.replace(/\D/g, "") })} data-testid={`pin-setup-${k}`} />
        ))}
        {err && <p className="text-red-400 text-xs mb-3" data-testid="pin-setup-error">{err}</p>}
        <button className="btn w-full" disabled={f.new.length < 4 || f.confirm.length < 4} onClick={save} data-testid="pin-setup-save">Save PIN</button>
      </div>
    </div>
  );
}

function FanGate({ queue, onAside }) {
  const act = useAct();
  const m = queue[0];
  const [custom, setCustom] = useState("");
  const [picking, setPicking] = useState(false);
  useEffect(() => { api.post(`/fan/${m.id}/view`).catch(() => {}); setPicking(false); setCustom(""); }, [m.id]);
  const handle = (reaction) => act("post", `/fan/${m.id}/handle`, { reaction });
  return (
    <div className="fixed inset-0 z-[70] bg-oh-bg flex items-center justify-center p-6 pt-safe pb-safe" data-testid="fan-gate">
      <div className="w-full max-w-sm pop-in" key={m.id}>
        <p className="label-xs text-center mb-3" data-testid="fan-gate-progress">Fan message · {queue.length} waiting</p>
        <div className="card p-6 text-center">
          <div className="flex justify-center mb-3"><UserLogo id={m.sender} size={44} /></div>
          <p className="text-lg leading-relaxed whitespace-pre-wrap break-words" data-testid="fan-gate-text">{m.text}</p>
          <p className="text-xs text-oh-text2 mt-3">{fmtStamp(m.created_at)}</p>
        </div>
        {picking ? (
          <div className="card p-4 mt-4" data-testid="fan-gate-emoji-picker">
            <div className="grid grid-cols-8 gap-1 text-2xl">
              {EMOJI.map((e) => <button key={e} onClick={() => handle(e)} className="p-1 active:scale-90 transition-transform" data-testid={`fan-emoji-${e}`}>{e}</button>)}
            </div>
            <div className="flex gap-2 mt-3">
              <input className="inp" placeholder="Any emoji" value={custom} maxLength={8} onChange={(e) => setCustom(e.target.value)} data-testid="fan-gate-custom-emoji" />
              <button className="btn" disabled={!custom.trim()} onClick={() => handle(custom.trim())} data-testid="fan-gate-custom-send">Send</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 mt-4">
            <button className="btn btn-ghost" onClick={() => handle(null)} data-testid="fan-gate-ignore">Ignore</button>
            <button className="btn btn-ghost" onClick={() => onAside(m.id)} data-testid="fan-gate-put-aside">Put Aside</button>
            <button className="btn" onClick={() => setPicking(true)} data-testid="fan-gate-react">React</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Celebration({ list }) {
  const act = useAct();
  return (
    <div className="fixed inset-0 z-[65] bg-black/80 flex items-center justify-center p-6" data-testid="celebration-modal">
      <div className="card p-8 text-center max-w-sm w-full pop-in">
        <Crown size={54} className="mx-auto mb-3 text-[#E0B354] float" />
        <h2 className="display text-4xl mb-2">You won!</h2>
        <p className="text-oh-text2 text-sm mb-5">Household champion for {list.map((c) => c.month).join(", ")}{list.some((c) => c.corrected) ? " (after a score correction)" : ""}.</p>
        <button className="btn w-full" onClick={() => act("post", "/me/celebrations/ack")} data-testid="celebration-ack">Celebrate</button>
      </div>
    </div>
  );
}

export default function Gate({ children }) {
  const { me } = useAuth();
  const q = useGet(me && !me.pin_must_change ? "/fan/queue" : null, { interval: 0 });
  const [aside, setAside] = useState(() => JSON.parse(sessionStorage.getItem("oh_aside") || "[]"));
  const putAside = (id) => { const n = [...aside, id]; sessionStorage.setItem("oh_aside", JSON.stringify(n)); setAside(n); };
  if (me.pin_must_change) return <PinSetup />;
  if (q.isLoading) return null;
  const queue = (q.data || []).filter((m) => !aside.includes(m.id));
  if (queue.length) return <FanGate queue={queue} onAside={putAside} />;
  return (
    <>
      {children}
      {me.celebrations?.length > 0 && <Celebration list={me.celebrations} />}
    </>
  );
}
